import React, { useState, useEffect } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Grid,
   Divider,
   CircularProgress,
   Alert,
   Chip,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import { getAuthHeaders } from '../../../utils/auth';
import { format } from 'date-fns';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';

interface BusinessCardStats {
   totalScans: number;
   uniqueScans: number;
   lastScan: string | null;
   scansByDate: {
      [date: string]: {
         total: number;
         unique: number;
         business_card: number;
         profile_qr: number;
         profile_link: number;
      };
   };
   scansBySource: {
      business_card: number;
      profile_qr: number;
      profile_link: number;
   };
   uniqueScansBySource: {
      business_card: number;
      profile_qr: number;
      profile_link: number;
   };
   recentScans: ScanRecord[];
}

interface ScanRecord {
   id: string;
   scan_id: string;
   scannedBy: string | null;
   scannerName: string;
   scannerEmail: string | null;
   scannerPhotoURL: string | null;
   scannedAt: string;
   isUnique: boolean;
   signedUp: boolean;
   source: 'business_card' | 'profile_qr' | 'profile_link';
}

interface ProfileBusinessCardTrackingProps {
   userId: string;
}

const ProfileBusinessCardTracking: React.FC<ProfileBusinessCardTrackingProps> = ({ userId }) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [stats, setStats] = useState<BusinessCardStats | null>(null);

   useEffect(() => {
      fetchBusinessCardStats();
   }, [userId]);

   const fetchBusinessCardStats = async () => {
      try {
         setLoading(true);
         setError(null);

         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}/business-card-stats`, {
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to fetch business card statistics');
         }

         const data = await response.json();
         setStats(data);
      } catch (err) {
         console.error('Error fetching business card stats:', err);
         setError('Failed to load business card statistics');
      } finally {
         setLoading(false);
      }
   };

   const getSourceChartData = (source: 'business_card' | 'profile_qr' | 'profile_link') => {
      if (!stats || !stats.scansByDate) return [];

      // Convert scansByDate to array and extract data for specific source
      const chartData = Object.entries(stats.scansByDate)
         .map(([date, data]) => ({
            date,
            scans: data[source] || 0,
            dateFormatted: format(new Date(date), 'MMM dd'),
         }))
         .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Get last 30 days
      return chartData.slice(-30);
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Alert severity='error' sx={{ mb: 3 }}>
            {error}
         </Alert>
      );
   }

   if (!stats) {
      return (
         <Alert severity='info' sx={{ mb: 3 }}>
            No business card statistics available
         </Alert>
      );
   }

   const businessCardData = getSourceChartData('business_card');
   const profileQRData = getSourceChartData('profile_qr');
   const shareLinkData = getSourceChartData('profile_link');

   return (
      <Box>
         {/* Header */}
         <Typography variant='h5' sx={{ mb: 3, fontWeight: 600 }}>
            📊 Profile Analytics
         </Typography>

         {/* Summary Statistics */}
         <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='body2' color='text.secondary' gutterBottom>
                        Total Scans
                     </Typography>
                     <Typography variant='h4' color='primary' sx={{ fontWeight: 600 }}>
                        {stats.totalScans || 0}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='body2' color='text.secondary' gutterBottom>
                        Unique Scans
                     </Typography>
                     <Typography variant='h4' color='primary' sx={{ fontWeight: 600 }}>
                        {stats.uniqueScans || 0}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='body2' color='text.secondary' gutterBottom>
                        Business Card Scans
                     </Typography>
                     <Typography variant='h4' sx={{ fontWeight: 600, color: '#01949A' }}>
                        {stats.scansBySource?.business_card || 0}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card>
                  <CardContent>
                     <Typography variant='body2' color='text.secondary' gutterBottom>
                        Profile QR Scans
                     </Typography>
                     <Typography variant='h4' sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                        {stats.scansBySource?.profile_qr || 0}
                     </Typography>
                  </CardContent>
               </Card>
            </Grid>
         </Grid>

         {/* Charts Section */}
         {(businessCardData.length > 0 || profileQRData.length > 0 || shareLinkData.length > 0) ? (
            <>
               {/* Business Card QR Activity */}
               {businessCardData.length > 0 && (
            <Card sx={{ mb: 4 }}>
               <CardContent>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                     }}
                  >
                     <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        💳 Business Card QR Activity
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Total: {stats.scansBySource?.business_card || 0} | Unique:{' '}
                        {stats.uniqueScansBySource?.business_card || 0}
                     </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <ResponsiveContainer width='100%' height={200}>
                     <AreaChart data={businessCardData}>
                        <defs>
                           <linearGradient id='colorBusinessCard' x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='5%' stopColor='#01949A' stopOpacity={0.8} />
                              <stop offset='95%' stopColor='#01949A' stopOpacity={0.1} />
                           </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                        <XAxis
                           dataKey='dateFormatted'
                           stroke='#6b7280'
                           style={{ fontSize: isMobile ? '10px' : '12px' }}
                        />
                        <YAxis stroke='#6b7280' style={{ fontSize: isMobile ? '10px' : '12px' }} />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                           }}
                        />
                        <Area
                           type='monotone'
                           dataKey='scans'
                           stroke='#01949A'
                           fillOpacity={1}
                           fill='url(#colorBusinessCard)'
                           name='Scans'
                        />
                     </AreaChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>
         )}

         {/* Profile QR Code Activity */}
         {profileQRData.length > 0 && (
            <Card sx={{ mb: 4 }}>
               <CardContent>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                     }}
                  >
                     <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        📱 Profile QR Code Activity
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Total: {stats.scansBySource?.profile_qr || 0} | Unique:{' '}
                        {stats.uniqueScansBySource?.profile_qr || 0}
                     </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <ResponsiveContainer width='100%' height={200}>
                     <AreaChart data={profileQRData}>
                        <defs>
                           <linearGradient id='colorProfileQR' x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='5%' stopColor='#8b5cf6' stopOpacity={0.8} />
                              <stop offset='95%' stopColor='#8b5cf6' stopOpacity={0.1} />
                           </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                        <XAxis
                           dataKey='dateFormatted'
                           stroke='#6b7280'
                           style={{ fontSize: isMobile ? '10px' : '12px' }}
                        />
                        <YAxis stroke='#6b7280' style={{ fontSize: isMobile ? '10px' : '12px' }} />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                           }}
                        />
                        <Area
                           type='monotone'
                           dataKey='scans'
                           stroke='#8b5cf6'
                           fillOpacity={1}
                           fill='url(#colorProfileQR)'
                           name='Scans'
                        />
                     </AreaChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>
         )}

         {/* Share Link Activity */}
         {shareLinkData.length > 0 && (
            <Card sx={{ mb: 4 }}>
               <CardContent>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                     }}
                  >
                     <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        🔗 Share Link Activity
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Total: {stats.scansBySource?.profile_link || 0} | Unique:{' '}
                        {stats.uniqueScansBySource?.profile_link || 0}
                     </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <ResponsiveContainer width='100%' height={200}>
                     <AreaChart data={shareLinkData}>
                        <defs>
                           <linearGradient id='colorShareLink' x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='5%' stopColor='#6366f1' stopOpacity={0.8} />
                              <stop offset='95%' stopColor='#6366f1' stopOpacity={0.1} />
                           </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                        <XAxis
                           dataKey='dateFormatted'
                           stroke='#6b7280'
                           style={{ fontSize: isMobile ? '10px' : '12px' }}
                        />
                        <YAxis stroke='#6b7280' style={{ fontSize: isMobile ? '10px' : '12px' }} />
                        <Tooltip
                           contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                           }}
                        />
                        <Area
                           type='monotone'
                           dataKey='scans'
                           stroke='#6366f1'
                           fillOpacity={1}
                           fill='url(#colorShareLink)'
                           name='Scans'
                        />
                     </AreaChart>
                  </ResponsiveContainer>
               </CardContent>
            </Card>
         )}
            </>
         ) : (
            <Card>
               <CardContent>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                     <Typography variant='h6' color='text.secondary' gutterBottom>
                        No activity yet
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Share your profile QR code or business card to start tracking analytics
                     </Typography>
                  </Box>
               </CardContent>
            </Card>
         )}
      </Box>
   );
};

export default ProfileBusinessCardTracking;
